import { reactive, ref, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

export function useRegisterLogic() {
    const router = useRouter()
    const instance = getCurrentInstance()
    const { $request,  $ValidConst } = instance.appContext.config.globalProperties

    // 表单数据
    const form = reactive({
        userName: '',          // 昵称
        phoneNumber: '',       // 手机号
        email: '',             // 邮箱
        userPwd: '',           // 密码
        confirmPwd: '',        // 确认密码
        code: ''               // 验证码
    })

    // 输入框引用
    const userNameInput = ref(null)
    const phoneNumberInput = ref(null)
    const emailInput = ref(null)
    const userPwdInput = ref(null)
    const confirmPwdInput = ref(null)
    const codeInput = ref(null)

    // 错误状态
    const fieldError = reactive({
        userName: false,
        phoneNumber: false,
        email: false,
        userPwd: false,
        confirmPwd: false,
        code: false
    })

    // 错误提示文本
    const errorText = reactive({
        userName: '',
        phoneNumber: '',
        email: '',
        userPwd: '',
        confirmPwd: '',
        code: ''
    })

    // 验证码倒计时相关
    const isCountingDown = ref(false)
    const countDown = ref(60)
    let countDownTimer = null

    // 全局状态
    const globalError = ref('')
    const isLoading = ref(false)
    // 用于触发动画的标志
    const shakeTrigger = ref(0)

    // 邮箱验证状态
    const isEmailValid = computed(() => {
        return form.email.trim() !== '' &&
            $ValidConst.EMAIL_REG.test(form.email.trim()) &&
            !fieldError.email;
    })

    // 验证码按钮文本
    const codeText = computed(() => {
        return isCountingDown.value ? `${countDown.value}秒后重新获取` : '获取验证码';
    })

    // 验证码按钮是否禁用
    const isCodeDisabled = computed(() => {
        return isCountingDown.value || !isEmailValid.value;
    })

    // 所有字段有效（控制提交状态）
    const isAllValid = computed(() => {
        return !fieldError.userName &&
            !fieldError.phoneNumber &&
            !fieldError.email &&
            !fieldError.userPwd &&
            !fieldError.confirmPwd &&
            !fieldError.code &&
            form.userName.trim() &&
            form.phoneNumber.trim() &&
            form.email.trim() &&
            form.userPwd.trim() &&
            form.confirmPwd.trim() &&
            form.code.trim()
    })

    // 输入处理（清空错误）
    const handleInput = (field) => {
        fieldError[field] = false
        errorText[field] = ''
        globalError.value = ''

        // 清空原生验证提示
        const inputEl = {
            userName: userNameInput.value,
            phoneNumber: phoneNumberInput.value,
            email: emailInput.value,
            userPwd: userPwdInput.value,
            confirmPwd: confirmPwdInput.value,
            code: codeInput.value
        }[field]
        if (inputEl) {
            inputEl.setCustomValidity('')
        }
    }

    // 字段校验（使用统一的错误提示）
    const checkField = (field) => {
        // 用户名验证
        if (field === 'userName') {
            if (form.userName.trim() === '') {
                errorText.userName = '请输入昵称'
                fieldError.userName = true
                return
            }
            fieldError.userName = false
            errorText.userName = ''
        }

        // 手机号验证
        if (field === 'phoneNumber') {
            if (form.phoneNumber.trim() === '') {
                errorText.phoneNumber = '请输入手机号'
                fieldError.phoneNumber = true
                return
            }
            if (!$ValidConst.PHONE_REG.test(form.phoneNumber.trim())) {
                errorText.phoneNumber = $ValidConst.PHONE_MSG
                fieldError.phoneNumber = true
                return
            }
            fieldError.phoneNumber = false
            errorText.phoneNumber = ''
        }

        // 邮箱验证
        if (field === 'email') {
            if (form.email.trim() === '') {
                errorText.email = '请输入邮箱'
                fieldError.email = true
                return
            }
            if (!$ValidConst.EMAIL_REG.test(form.email.trim())) {
                errorText.email = $ValidConst.EMAIL_MSG
                fieldError.email = true
                return
            }
            fieldError.email = false
            errorText.email = ''
        }

        // 密码验证
        if (field === 'userPwd') {
            if (form.userPwd.trim() === '') {
                errorText.userPwd = '请输入密码'
                fieldError.userPwd = true
                return
            }
            if (!$ValidConst.PWD_REG.test(form.userPwd.trim())) {
                errorText.userPwd = $ValidConst.PWD_MSG
                fieldError.userPwd = true
                return
            }
            // 验证确认密码
            if (form.confirmPwd.trim() !== '') {
                checkField('confirmPwd')
            }
            fieldError.userPwd = false
            errorText.userPwd = ''
        }

        // 确认密码验证
        if (field === 'confirmPwd') {
            if (form.confirmPwd.trim() === '') {
                errorText.confirmPwd = '请确认密码'
                fieldError.confirmPwd = true
                return
            }
            if (form.confirmPwd.trim() !== form.userPwd.trim()) {
                errorText.confirmPwd = '两次输入的密码不一致'
                fieldError.confirmPwd = true
                return
            }
            fieldError.confirmPwd = false
            errorText.confirmPwd = ''
        }

        // 验证码验证
        if (field === 'code') {
            if (form.code.trim() === '') {
                errorText.code = '请输入验证码'
                fieldError.code = true
                return
            }
            if (!$ValidConst.CODE_REG.test(form.code.trim())) {
                errorText.code = $ValidConst.CODE_MSG
                fieldError.code = true
                return
            }
            fieldError.code = false
            errorText.code = ''
        }
    }

    // 失去焦点校验
    const validateField = (field) => {
        // 仅在提交后才校验空值
        if (!isLoading.value && form[field].trim() === '') {
            return
        }
        checkField(field)
    }

    // 设置全局错误并触发抖动
    const setGlobalError = (message) => {
        // 先清空错误，触发重绘
        globalError.value = ''
        instance.proxy.$nextTick(() => {
            globalError.value = message
            // 每次设置错误时增加触发器值，触发动画
            shakeTrigger.value++
        })
    }

    // 获取验证码
    const getVerificationCode = async () =>  {
        checkField('email')
        if (!isEmailValid.value) return

        try {
            isCountingDown.value = true
            // 请求封装成表单格式
            const formData = new FormData();
            formData.append('email', form.email);
            const res = await $request.post('/login/sendCode', formData);

            if (res && res.code === 200) {
                setGlobalError(res.msg || '验证码已发送，请查收')
                let count = 60
                countDown.value = count

                // 清除可能存在的旧定时器
                if (countDownTimer) clearInterval(countDownTimer)

                countDownTimer = setInterval(() => {
                    count--
                    countDown.value = count
                    if (count <= 0) {
                        clearInterval(countDownTimer)
                        isCountingDown.value = false
                        countDownTimer = null
                    }
                }, 1000)
            } else {
                setGlobalError(res.msg || '发送验证码失败，请重试')
                isCountingDown.value = false
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '发送验证码失败，请重试')
            isCountingDown.value = false
        }
    }

    // 处理注册
    const handleRegister = async () => {
        // 清除之前的错误提示
        Object.keys(fieldError).forEach(key => {
            fieldError[key] = false
            errorText[key] = ''
        })
        globalError.value = ''

        // 全量校验
        checkField('userName')
        checkField('phoneNumber')
        checkField('email')
        checkField('userPwd')
        checkField('confirmPwd')
        checkField('code')

        // 检查是否有错误
        if (!isAllValid.value) {
            return
        }

        try {
            isLoading.value = true
            // 实际项目中替换为真实接口请求
            const res = await $request.post('/login/register', {
                userName: form.userName,
                phoneNumber: form.phoneNumber,
                email: form.email,
                userPwd: form.userPwd,
                code: form.code
            })

            if (res && res.code === 200) {
                setGlobalError('注册成功，请登录')
                setTimeout(() => {
                    router.push('/login')
                }, 1500)
            } else {
                setGlobalError(res.msg || '注册失败，请稍后重试')
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '注册失败，请稍后重试')
        } finally {
            isLoading.value = false
        }
    }

    // 前往登录页面
    const goToLogin = () => {
        router.push('/login')
    }

    // 清理定时器
    const cleanup = () => {
        if (countDownTimer) {
            clearInterval(countDownTimer)
            countDownTimer = null
        }
    }

    // 组件卸载时清理
    window.addEventListener('beforeunload', cleanup)

    return {
        form,
        fieldError,
        errorText,
        globalError,
        shakeTrigger,  // 导出动画触发器
        isLoading,
        handleRegister,
        goToLogin,
        handleInput,
        validateField,
        userNameInput,
        phoneNumberInput,
        emailInput,
        userPwdInput,
        confirmPwdInput,
        codeInput,
        isCountingDown,
        countDown,
        getVerificationCode,
        isAllValid,
        codeText,
        isCodeDisabled,
        isEmailValid
    }
}